/**
 * How a timesheet is written down.
 *
 * Five formatters, in one place because there are now two screens reading the
 * same payload: **My hours** on a crew member's phone, and the **Hours** tab
 * on a person in Settings › Users & roles. They print the same week and they
 * have to print it the same way — a supervisor and the person they supervise
 * arguing about a number that is rounded differently on two screens is the
 * one argument a timesheet must never cause.
 */
import { parseDateOnly } from './format.js'

/** "26.4 h" — one decimal, which is how a timesheet is read and argued about. */
export const hoursLabel = (minutes: number): string => `${(minutes / 60).toFixed(1)} h`

/** "1h 42m" — an open punch is counted in minutes, because it is still moving. */
export function shortDuration(minutes: number): string {
  const whole = Math.max(0, Math.round(minutes))
  return `${Math.floor(whole / 60)}h ${String(whole % 60).padStart(2, '0')}m`
}

export const clockTime = (value: string | null): string => {
  if (!value) return '—'
  return new Date(value).toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit' })
    .toLowerCase()
}

export const weekdayLabel = (date: string): string =>
  parseDateOnly(date)?.toLocaleDateString('en-US', { weekday: 'short', timeZone: 'UTC' }) ?? '—'

/** "Sep 14 – 20", dropping the month on the end when the week does not cross one. */
export function weekLabel(week: { start: string; end: string }): string {
  const start = parseDateOnly(week.start)
  const end = parseDateOnly(week.end)
  if (!start || !end) return ''
  const opts = { month: 'short', day: 'numeric', timeZone: 'UTC' } as const
  const left = start.toLocaleDateString('en-US', opts)
  const right = start.getUTCMonth() === end.getUTCMonth()
    ? String(end.getUTCDate())
    : end.toLocaleDateString('en-US', opts)
  return `${left} – ${right}`
}

/** Any date inside the week you want; the server still finds its Monday. */
export function shiftWeeks(from: Date, weeks: number): string {
  const moved = new Date(from)
  moved.setDate(moved.getDate() + weeks * 7)
  return moved.toISOString().slice(0, 10)
}

/** One day of the week, as `GET /crew-app/my-hours` answers it. */
export interface HoursDay {
  date: string
  firstIn: string
  lastOut: string | null
  open: boolean
  stops: number
  minutes: number
  breakMinutes: number
  correction?: { reason: string | null; byName: string | null }
}

export interface HoursPayload {
  user: { id: string; name: string } | null
  crew: string | null
  leadName: string | null
  timeZone: string
  today: string
  week: { start: string; end: string }
  standardHours: number
  overtimeAfterHours: number
  totalMinutes: number
  daysLeft: number
  openSince: string | null
  openAt: string | null
  days: HoursDay[]
}
